import { Popover, Tooltip } from "antd";
import {
  AppstoreFilled,
  TeamOutlined,
  SafetyCertificateFilled,
  ContactsFilled,
  StarFilled,
  KeyOutlined,
  EditFilled,
  QuestionCircleFilled,
  BellFilled,
  UserOutlined,
  LogoutOutlined,
  PictureFilled,
  BarChartOutlined,
  ProfileFilled,
  HistoryOutlined,
} from "@ant-design/icons";
import { useContext, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

import SingleLogo from "../../assets/images/single-logo.svg";
import { AuthContext } from "../../context/AuthContext";
import lang from "../../helper/lang";
import DeleteModal from "../../components/DeleteModal";

export const menuItems = [
  { key: "Dashboard",       path: "/dashboard",        icon: AppstoreFilled,         label: lang("Dashboard"),        shortLabel: lang("Home") },
  { key: "adminManager",    path: "/team-manager",     icon: TeamOutlined,            label: lang("Team Manager"),     shortLabel: lang("Team") },
  { key: "RoleManager",     path: "/role-manager",     icon: SafetyCertificateFilled, label: lang("Role Manager"),     shortLabel: lang("Roles") },
  { key: "customerManager", path: "/customer-manager", icon: ContactsFilled,          label: lang("Customer Manager"), shortLabel: lang("Clients") },
  { key: "brandManager",    path: "/brand-manager",    icon: StarFilled,              label: lang("Brand Manager"),    shortLabel: lang("Brands") },
  { key: "onboarding",      path: "/onboarding-management", icon: ProfileFilled,      label: lang("Onboarding"),       shortLabel: lang("Onboard") },
  { key: "accessModule",    path: "/access-module",    icon: KeyOutlined,             label: lang("Access Module"),    shortLabel: lang("Access") },
  { key: "ContentManager",  path: "/content",          icon: EditFilled,              label: lang("Content Manager"),  shortLabel: lang("Content") },
  { key: "helpCenter",      path: "/help-center",      icon: QuestionCircleFilled,    label: lang("Help Center"),      shortLabel: lang("Help") },
  { key: "GraphicsRequests", permission: "Graphics", path: "/graphics-requests", icon: PictureFilled,     label: lang("Graphics Requests"), shortLabel: lang("Graphics") },
  { key: "BlogWriteRequests", permission: "Blog Write", path: "/blog-write-requests", icon: PictureFilled, label: lang("Blogs"), shortLabel: lang("Blogs") },
  { key: "Workload", permission: ["Graphics", "Blog Write"], path: "/workload", icon: BarChartOutlined, label: lang("Analytics"), shortLabel: lang("Analytics") },
  { key: "ActivityLog",     path: "/activity-log",     icon: HistoryOutlined,         label: lang("Activity Log"),     shortLabel: lang("Activity") },
];

const utilityItems = [
  { key: "notifications", path: "/notification", icon: BellFilled, label: lang("Notifications"), shortLabel: lang("Alerts") },
];

function Sidenav({ color }) {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  const storedProfile = JSON.parse(localStorage.getItem("admin_userProfile") || "null");
  const userProfile = storedProfile || {};
  const userPermission = userProfile?.roleId?.permission || [];
  const type = userProfile?.type || "";

  const permissionMap = userPermission.reduce((acc, item) => {
    acc[item.name] = item;
    return acc;
  }, {});

  const filteredItems = menuItems.filter((item) => {
    if (type === "Admin") return true;
    // Workload's nav entry is shared by both the Graphics Team and Blog
    // Write Team tabs, so it needs to show for holders of either permission
    // — a single permission name here would hide the whole page from one
    // of the two teams.
    const permissionNames = Array.isArray(item.permission) ? item.permission : [item.permission || item.key];
    const permissions = permissionNames.map((name) => permissionMap[name]);
    if (permissions.every((p) => !p)) return true;
    return permissions.some((p) => p?.view);
  });

  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const initial = (userProfile?.name || userProfile?.email || "A").trim().charAt(0).toUpperCase();
  const firstName = (userProfile?.name || "Admin").split(" ")[0];

  const accountPopoverContent = (
    <div className="rail-account-popover">
      <div className="rail-account-popover__header">
        <div className="rail-account-popover__avatar">
          {userProfile?.profilePic ? (
            <img src={userProfile.profilePic} alt={firstName} />
          ) : (
            <span>{initial}</span>
          )}
        </div>
        <div>
          <div className="rail-account-popover__name">{userProfile?.name || lang("Admin")}</div>
          <div className="rail-account-popover__email">{userProfile?.email}</div>
        </div>
      </div>
      <div className="rail-account-popover__divider" />
      <NavLink
        to="/profile"
        className="rail-account-popover__link"
        onClick={() => setPopoverOpen(false)}
      >
        <UserOutlined />
        {lang("My Profile")}
      </NavLink>
      <button
        type="button"
        className="rail-account-popover__link rail-account-popover__link--danger"
        onClick={() => {
          setPopoverOpen(false);
          setIsLogoutModalVisible(true);
        }}
      >
        <LogoutOutlined />
        {lang("Logout")}
      </button>
    </div>
  );

  return (
    <div className="sidenav-rail">
      <div className="rail-logo">
        <NavLink to="/dashboard" className="rail-logo-link" title="Imergey Admin">
          <img src={SingleLogo} alt="Imergey" />
        </NavLink>
      </div>

      <div className="rail-scroll">
        <nav className="rail-nav">
          {filteredItems.map((item) => (
            <Tooltip key={item.key} title={item.label} placement="right">
              <NavLink to={item.path} className="rail-item">
                <span className="rail-icon">
                  <item.icon />
                </span>
                <span className="rail-item__label">{item.shortLabel}</span>
              </NavLink>
            </Tooltip>
          ))}
        </nav>

        <div className="rail-divider" />

        <nav className="rail-nav rail-nav--utility">
          {utilityItems.map((item) => (
            <Tooltip key={item.key} title={item.label} placement="right">
              <NavLink to={item.path} className="rail-item">
                <span className="rail-icon">
                  <item.icon />
                </span>
                <span className="rail-item__label">{item.shortLabel}</span>
              </NavLink>
            </Tooltip>
          ))}
        </nav>
      </div>

      <div className="rail-bottom">
        <Popover
          content={accountPopoverContent}
          trigger="hover"
          placement="rightBottom"
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          overlayClassName="rail-account-popover-overlay"
        >
          <button type="button" className="rail-avatar" aria-label={lang("Account menu")}>
            {userProfile?.profilePic ? (
              <img src={userProfile.profilePic} alt={firstName} />
            ) : (
              <span>{initial}</span>
            )}
          </button>
        </Popover>
      </div>

      {isLogoutModalVisible && (
        <DeleteModal
          title={lang("Logout")}
          subtitle={lang("Are you sure you want to logout?")}
          show={isLogoutModalVisible}
          hide={() => setIsLogoutModalVisible(false)}
          onOk={() => logout()}
        />
      )}
    </div>
  );
}

export default Sidenav;
